import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../SvgIcon";
import ExerciseLibraryMetadataFields from "./ExerciseLibraryMetadataFields";
import { areMetadataMissingKeysEqual } from "../../../../utils/exercise-library-metadata";

/**
 * Collapsible library metadata editor when catalog fields are incomplete.
 *
 * Parent (`add-exercise`) sets `key={libraryMetadataSelectionKey(form)}` so this
 * subtree remounts when the coach picks another exercise: panel closed, draft reset,
 * no stale edits from the previous selection.
 */
const ExerciseLibraryMetadataGapsNotice = forwardRef(
  function ExerciseLibraryMetadataGapsNotice(
    {
      missingKeys,
      record,
      libraryIndex,
      formPrimary,
      isPrimaryExercise,
      repMaximum,
      athleteId,
    },
    ref
  ) {
    const { t } = useTranslation("coaching-portal");
    // Ref to the inner form (exposes getSavePayload + submitLibraryMetadata).
    const fieldsRef = useRef(null);
    const [open, setOpen] = useState(false);
    // "X missing" chip: starts from library gaps; child updates it while editing.
    const [activeMissingKeys, setActiveMissingKeys] = useState(missingKeys);
    // Create a signature of the missing keys to track changes.
    const missingKeysSignature = useMemo(
      () => missingKeys.join(","),
      [missingKeys]
    );
    /** Handle the missing keys change. */
    const handleMissingKeysChange = useCallback((keys) => {
      setActiveMissingKeys((prev) =>
        areMetadataMissingKeysEqual(prev, keys) ? prev : keys
      );
    }, []);

    // forwardRef: add-exercise (class) reads the draft only on Save via ref, not parent state.
    /**
     * Ref bridge for add-exercise Save → metadata panel.
     *
     * add-exercise calls these through `libraryMetadataPanelRef` on Save only.
     * `submitLibraryMetadata` forwards to Fields → `fetchLibraryMetadataAction`
     * (Rill in-browser POST; see submit-library-metadata-from-add-exercise.js).
     */
    useImperativeHandle(
      ref,
      () => ({
        /** @returns {object|null} Normalized save payload from the metadata panel ref. */
        getSavePayload() {
          return fieldsRef.current?.getSavePayload?.() ?? null;
        },
        /**
         * Pass-through to Fields.submitLibraryMetadata (Rill POST + optional video file).
         *
         * @param {string} url
         * @param {Record<string, string>} body
         * @param {object} req — `this.context.req` from add-exercise
         * @returns {Promise<unknown>|undefined}
         */
        submitLibraryMetadata(url, body, req) {
          return fieldsRef.current?.submitLibraryMetadata?.(url, body, req);
        },
      }),
      []
    );

    // Reset chip when the library record gaps change (new exercise), not on array reference churn.
    useEffect(() => {
      setActiveMissingKeys((prev) =>
        areMetadataMissingKeysEqual(prev, missingKeys) ? prev : missingKeys
      );
      // missingKeys read when missingKeysSignature changes (eslint wants missingKeys in deps).
    }, [missingKeysSignature, missingKeys]);

    return (
      <div
        className="Add-Exercise-metadataDetails"
        data-testid="exercise-library-metadata-gaps"
      >
        <button
          type="button"
          className="Add-Exercise-metadataDetails-summary"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <SvgIcon
            src="/img/svg/angle-right.svg"
            classNames={`Add-Exercise-metadataDetails-chevron${open ? " Add-Exercise-metadataDetails-chevron--open" : ""
              }`}
          />
          <span className="Add-Exercise-metadataDetails-heading">
            <span className="Add-Exercise-metadataDetails-title s2">
              {t("addExerciseModal.exerciseDetailsTitle")}
            </span>
            {activeMissingKeys.length > 0 ? (
              <span className="chip warning Add-Exercise-metadataDetails-chip">
                {t("addExerciseModal.metadataMissingCount", {
                  count: activeMissingKeys.length,
                })}
              </span>
            ) : null}
          </span>
        </button>
        {open ? (
          <div
            className="Add-Exercise-metadataDetails-divider"
            aria-hidden="true"
          />
        ) : null}
        <div
          className={
            open
              ? "Add-Exercise-metadataDetails-panelWrap"
              : "Add-Exercise-metadataDetails-panelWrap hidden"
          }
          aria-hidden={!open}
        >
          <ExerciseLibraryMetadataFields
            ref={fieldsRef}
            record={record}
            libraryIndex={libraryIndex}
            formPrimary={formPrimary}
            isPrimaryExercise={isPrimaryExercise}
            repMaximum={repMaximum}
            athleteId={athleteId}
            isOpen={open}
            onMissingKeysChange={handleMissingKeysChange}
          />
        </div>
      </div>
    );
  }
);

ExerciseLibraryMetadataGapsNotice.propTypes = {
  missingKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  record: PropTypes.object,
  libraryIndex: PropTypes.number,
  formPrimary: PropTypes.string,
  isPrimaryExercise: PropTypes.bool,
  repMaximum: PropTypes.number,
  athleteId: PropTypes.string,
};

export default ExerciseLibraryMetadataGapsNotice;
